/**
 * Water Report Page Form
 * Full-page version: validates email + zipcode, sends to n8n,
 * shows loading screen with step animation, redirects to report.
 */
(function() {
  'use strict';

  var CONFIG = window.WaterReportConfig;
  if (!CONFIG) {
    console.error('[WR Page] Configuration not found. Make sure water-report-config.js is loaded.');
    return;
  }

  /* ── State ── */
  var isPostcodeValid = false;
  var validationTimeout = null;
  var market = 'UK';

  /* ── DOM refs (set in init) ── */
  var formState, loadingState, errorState;
  var form, submitBtn, zipcodeInput;

  /* ===================================================
     POSTCODE VALIDATION (same logic as popup)
     =================================================== */

  function validatePostcode(postcode) {
    var clean = postcode.trim().replace(/\s+/g, '');
    if (clean.length < 5) return Promise.resolve({ valid: false, message: '' });

    if (market === 'US') {
      var valid = /^[0-9]{5}(-[0-9]{4})?$/.test(clean);
      return Promise.resolve({ valid: valid, message: valid ? '' : 'Invalid US ZIP code' });
    }

    return fetch(CONFIG.postcodeApiUrl + '/' + encodeURIComponent(clean) + '/validate')
      .then(function(r) { return r.json(); })
      .then(function(data) {
        return { valid: data.result === true, message: data.result === true ? '' : 'Invalid UK postcode' };
      })
      .catch(function() {
        return { valid: false, message: 'Unable to validate postcode' };
      });
  }

  function updatePostcodeUI(state) {
    if (!zipcodeInput) return;
    zipcodeInput.classList.remove('is-valid', 'is-invalid', 'is-validating');

    var group = zipcodeInput.closest('.wrp-input-group');
    var errorEl = group ? group.querySelector('.wrp-postcode-error') : null;

    if (state === 'validating') {
      zipcodeInput.classList.add('is-validating');
      if (errorEl) errorEl.textContent = '';
    } else if (state === 'valid') {
      zipcodeInput.classList.add('is-valid');
      if (errorEl) errorEl.textContent = '';
    } else if (state === 'invalid') {
      zipcodeInput.classList.add('is-invalid');
    } else {
      if (errorEl) errorEl.textContent = '';
    }

    updateSubmitButton();
  }

  function showPostcodeError(message) {
    if (!zipcodeInput) return;
    var group = zipcodeInput.closest('.wrp-input-group');
    var errorEl = group ? group.querySelector('.wrp-postcode-error') : null;

    if (!errorEl && group) {
      errorEl = document.createElement('span');
      errorEl.className = 'wrp-postcode-error';
      group.appendChild(errorEl);
    }
    if (errorEl) errorEl.textContent = message;
  }

  function handlePostcodeInput() {
    isPostcodeValid = false;
    if (validationTimeout) clearTimeout(validationTimeout);

    var value = zipcodeInput ? zipcodeInput.value.trim() : '';
    if (value.length < 5) {
      updatePostcodeUI('neutral');
      return;
    }

    updatePostcodeUI('validating');

    validationTimeout = setTimeout(function() {
      validatePostcode(value).then(function(result) {
        isPostcodeValid = result.valid;
        if (result.valid) {
          updatePostcodeUI('valid');
        } else {
          updatePostcodeUI('invalid');
          if (result.message) showPostcodeError(result.message);
        }
      });
    }, CONFIG.debounceDelay);
  }

  function updateSubmitButton() {
    if (!submitBtn) return;
    submitBtn.disabled = !isPostcodeValid;
  }

  /* ===================================================
     UI STATE TRANSITIONS
     =================================================== */

  function showLoading() {
    formState.style.display = 'none';
    errorState.style.display = 'none';
    loadingState.style.display = 'block';

    // Inject Google Maps satellite into radar with user's zipcode
    var radarMap = document.getElementById('wrp-radar-map');
    var zipcode = zipcodeInput ? zipcodeInput.value.trim() : '';
    if (radarMap && zipcode) {
      radarMap.innerHTML = '<iframe src="https://maps.google.com/maps?q=' + encodeURIComponent(zipcode) + '&t=k&z=15&hl=en&output=embed" width="100%" height="100%" style="border:0;" loading="eager"></iframe>';
    }

    // Step animation is handled by inline script in the snippet (MutationObserver)
  }

  function showError() {
    formState.style.display = 'none';
    loadingState.style.display = 'none';
    errorState.style.display = 'block';
  }

  function showForm() {
    loadingState.style.display = 'none';
    errorState.style.display = 'none';
    formState.style.display = '';
  }

  /* ===================================================
     SUPABASE TRACKING (fire-and-forget)
     =================================================== */

  function trackFormSubmission(email) {
    var sessionId = getSessionId();
    try {
      fetch(CONFIG.supabaseFormSubmissionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + CONFIG.supabaseAnonKey,
          'apikey': CONFIG.supabaseAnonKey
        },
        body: JSON.stringify({ popup_session_id: sessionId, email: email })
      });
    } catch (e) { /* silent */ }
  }

  function getSessionId() {
    try {
      var existing = localStorage.getItem(CONFIG.sessionIdKey);
      if (existing) return existing;
      var id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : generateUUID();
      localStorage.setItem(CONFIG.sessionIdKey, id);
      return id;
    } catch (e) {
      return generateUUID();
    }
  }

  function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      var r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }

  /* ===================================================
     FORM SUBMISSION
     =================================================== */

  function handleSubmit(e) {
    e.preventDefault();
    if (!isPostcodeValid) return;

    var data = {
      email: '',
      phone: '',
      zipcode: zipcodeInput.value.trim(),
      popup_session_id: getSessionId(),
      source: 'page',
      market: market
    };

    if (!data.zipcode) return;

    // Show loading
    showLoading();
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Track in Supabase (fire-and-forget)
    trackFormSubmission(data.email);

    // Two things happen in parallel:
    // 1. n8n webhook (returns token)
    // 2. Step animation (finishes at ~4s)
    // We redirect as soon as BOTH are done.

    var submitTime = Date.now();
    var minWait = 4000; // minimum time to show animation (ms)

    var webhookUrl = (market === 'US' && CONFIG.webhookUrlUS) ? CONFIG.webhookUrlUS : CONFIG.webhookUrl;

    fetch(webhookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })
    .then(function(response) {
      if (!response.ok) throw new Error('Webhook returned ' + response.status);
      return response.json();
    })
    .then(function(result) {
      var token = result.token || result.Token || result.report_token;
      if (!token) throw new Error('No token in response');

      try {
        var key = CONFIG.storageKey + '_' + market.toLowerCase();
        localStorage.setItem(key + '_submitted', 'true');
      } catch (e) { /* silent */ }

      // How long since submit?
      var elapsed = Date.now() - submitTime;
      var remaining = Math.max(0, minWait - elapsed);

      // Wait for animation to finish, then redirect
      setTimeout(function() {
        var loadingTitle = document.getElementById('wrp-loading-title');
        if (loadingTitle) loadingTitle.textContent = 'Your report is nearly ready!';
        setTimeout(function() {
          window.location.href = '/apps/water-report?token=' + encodeURIComponent(token);
        }, 1000);
      }, remaining);
    })
    .catch(function(err) {
      console.error('[WR Page] Webhook error:', err);
      showError();
    });
  }

  /* ===================================================
     INIT
     =================================================== */

  function init() {
    formState = document.getElementById('wrp-form-state');
    loadingState = document.getElementById('wrp-loading-state');
    errorState = document.getElementById('wrp-error-state');
    form = document.getElementById('wrp-form');
    submitBtn = form ? form.querySelector('.wrp-form-submit') : null;
    zipcodeInput = document.getElementById('wrp-zipcode');

    if (!form || !formState || !loadingState || !errorState) {
      console.warn('[WR Page] Required elements not found');
      return;
    }

    // Detect market
    var shopifyCountry = (typeof Shopify !== 'undefined' && Shopify.country) ? Shopify.country : null;
    if (shopifyCountry === 'US') {
      market = 'US';
    } else {
      market = 'UK';
    }
    form.dataset.market = market;

    // Adapt UI for US market
    if (market === 'US') {
      if (zipcodeInput) {
        zipcodeInput.placeholder = 'Your ZIP code (e.g. 10001)';
        zipcodeInput.pattern = '[0-9]{5}(-[0-9]{4})?';
        zipcodeInput.maxLength = 10;
      }
      var badge = document.getElementById('wrp-badge-text');
      if (badge) badge.textContent = '50,000+ US homes tested';
    }

    // Event listeners
    form.addEventListener('submit', handleSubmit);
    if (zipcodeInput) zipcodeInput.addEventListener('input', handlePostcodeInput);

    // Retry button
    var retryBtn = document.getElementById('wrp-retry-btn');
    if (retryBtn) {
      retryBtn.addEventListener('click', function() {
        isPostcodeValid = false;
        updateSubmitButton();
        showForm();
      });
    }

    // Initial button state
    updateSubmitButton();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
